import React, { useState, useEffect } from 'react';
import { X, Save, Trash2, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './JournalDrawer.css';

/**
 * JournalDrawer — Modal for taking timestamped notes on passages
 * Auto-saves to localStorage, syncs to Supabase
 * Notes auto-delete after 15 days unless saved with password
 */
const JournalDrawer = ({ isOpen, onClose, passageRef = '' }) => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState([]);
  const [savedNotes, setSavedNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savePassword, setSavePassword] = useState('');

  // Load journal from localStorage on mount
  useEffect(() => {
    if (isOpen) {
      try {
        const stored = localStorage.getItem('kairos_journal');
        if (stored) {
          const { notes: n = [], savedNotes: s = [] } = JSON.parse(stored);
          setNotes(n);
          setSavedNotes(s);
        }
      } catch (e) {
        console.warn('Failed to load journal from localStorage:', e);
      }
    }
  }, [isOpen]);

  // Save note
  const addNote = () => {
    if (!newNoteText.trim()) return;

    const note = {
      id: `note-${Date.now()}`,
      timestamp: new Date().toISOString(),
      text: newNoteText.trim(),
      passageRef: passageRef || 'General',
    };

    const updatedNotes = [...notes, note];
    setNotes(updatedNotes);
    setNewNoteText('');

    // Persist to localStorage immediately
    persistJournal(updatedNotes, savedNotes);

    // Auto-sync to server (non-blocking)
    syncToServer(updatedNotes, savedNotes);
  };

  // Delete note
  const deleteNote = (noteId) => {
    const updatedNotes = notes.filter(n => n.id !== noteId);
    setNotes(updatedNotes);
    persistJournal(updatedNotes, savedNotes);
    syncToServer(updatedNotes, savedNotes);
  };

  // Save note permanently (with optional password)
  const saveNotePermanently = (noteId) => {
    const note = notes.find(n => n.id === noteId);
    if (!note) return;

    const savedNote = {
      ...note,
      id: `saved-${Date.now()}`,
      password: savePassword ? btoa(savePassword) : null, // Simple base64 (should be bcrypt server-side)
    };

    const updatedSavedNotes = [...savedNotes, savedNote];
    const updatedNotes = notes.filter(n => n.id !== noteId);

    setSavedNotes(updatedSavedNotes);
    setNotes(updatedNotes);
    setShowSaveModal(false);
    setSavePassword('');

    persistJournal(updatedNotes, updatedSavedNotes);
    syncToServer(updatedNotes, updatedSavedNotes);
  };

  // Persist to localStorage
  const persistJournal = (n, s) => {
    localStorage.setItem('kairos_journal', JSON.stringify({
      lastUpdated: new Date().toISOString(),
      notes: n,
      savedNotes: s,
    }));
  };

  // Sync to server
  const syncToServer = async (n, s) => {
    try {
      await axios.post('/api/journal', {
        notes: n,
        savedNotes: s,
      });
    } catch (e) {
      console.warn('Journal sync failed:', e.message);
      // Graceful failure - localStorage is still effective
    }
  };

  // Delete all notes
  const clearAllNotes = () => {
    if (window.confirm(t('journal.confirmClearAll', 'Clear all notes?'))) {
      setNotes([]);
      setSavedNotes([]);
      persistJournal([], []);
      syncToServer([], []).catch(() => {});
    }
  };

  if (!isOpen) return null;

  return (
    <div className="journal-overlay" onClick={onClose}>
      <div className="journal-drawer" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="journal-header">
          <h2>{t('journal.title', 'Joernaal')}</h2>
          <button className="journal-close" onClick={onClose}><X size={20} /></button>
        </div>

        {error && <div className="journal-error">{error}</div>}

        {/* Content */}
        <div className="journal-content">
          {/* Active Notes Section */}
          <div className="journal-section">
            <h3>{t('journal.activeNotes', 'My Notes')} ({notes.length})</h3>
            {notes.length === 0 ? (
              <p className="journal-empty">{t('journal.noNotes', 'No notes yet. Start typing below!')}</p>
            ) : (
              <div className="journal-notes-list">
                {notes.map((note) => (
                  <div key={note.id} className="journal-note-card">
                    <div className="journal-note-meta">
                      <span className="journal-note-passage">{note.passageRef}</span>
                      <span className="journal-note-time">
                        {new Date(note.timestamp).toLocaleDateString()} {new Date(note.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="journal-note-text">{note.text}</p>
                    <div className="journal-note-actions">
                      <button
                        className="journal-btn-save"
                        onClick={() => {
                          setShowSaveModal(true);
                        }}
                        title={t('journal.savePermanently', 'Save permanently')}
                      >
                        <Lock size={16} /> {t('journal.save', 'Save')}
                      </button>
                      <button
                        className="journal-btn-delete"
                        onClick={() => deleteNote(note.id)}
                        title={t('journal.deleteNote', 'Delete this note')}
                      >
                        <Trash2 size={16} /> {t('journal.delete', 'Delete')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Saved Notes Section */}
          {savedNotes.length > 0 && (
            <div className="journal-section">
              <h3>{t('journal.savedNotes', 'Saved Notes')} ({savedNotes.length})</h3>
              <div className="journal-notes-list">
                {savedNotes.map((note) => (
                  <div key={note.id} className="journal-note-card journal-note-saved">
                    <div className="journal-note-meta">
                      <span className="journal-note-passage">{note.passageRef}</span>
                      {note.password && <span className="journal-note-lock">🔒</span>}
                    </div>
                    <p className="journal-note-text">{note.text}</p>
                    <div className="journal-note-actions">
                      <button
                        className="journal-btn-delete"
                        onClick={() => {
                          const updated = savedNotes.filter(n => n.id !== note.id);
                          setSavedNotes(updated);
                          persistJournal(notes, updated);
                          syncToServer(notes, updated);
                        }}
                      >
                        <Trash2 size={16} /> {t('journal.delete', 'Delete')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New Note Input */}
          <div className="journal-section">
            <h3>{t('journal.addNote', 'Add Note')}</h3>
            <textarea
              className="journal-textarea"
              placeholder={t('journal.placeholder', 'Write your thoughts about this passage...')}
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              rows={4}
            />
            <div className="journal-actions">
              <button
                className="journal-btn-primary"
                onClick={addNote}
                disabled={!newNoteText.trim()}
              >
                <Save size={16} /> {t('journal.createNote', 'Create Note')}
              </button>
              {(notes.length > 0 || savedNotes.length > 0) && (
                <button className="journal-btn-secondary" onClick={clearAllNotes}>
                  {t('journal.clearAll', 'Clear All')}
                </button>
              )}
            </div>
          </div>

          {/* Auto-delete notice */}
          <div className="journal-notice">
            <p>💡 {t('journal.autoDeleteNotice', 'Notes are automatically deleted after 15 days unless saved.')}</p>
          </div>
        </div>

        {/* Save Password Modal */}
        {showSaveModal && (
          <div className="journal-modal-overlay" onClick={() => setShowSaveModal(false)}>
            <div className="journal-modal" onClick={(e) => e.stopPropagation()}>
              <h3>{t('journal.savePermanently', 'Save Permanently (Optional Password)')}</h3>
              <input
                type="password"
                placeholder={t('journal.passwordPlaceholder', 'Optional password (leave blank for no password)')}
                value={savePassword}
                onChange={(e) => setSavePassword(e.target.value)}
                className="journal-modal-input"
              />
              <div className="journal-modal-actions">
                <button
                  className="journal-btn-primary"
                  onClick={() => saveNotePermanently(notes[0]?.id)}
                >
                  {t('journal.confirmSave', 'Save')}
                </button>
                <button className="journal-btn-secondary" onClick={() => setShowSaveModal(false)}>
                  {t('journal.cancel', 'Cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JournalDrawer;
